import { motion } from 'framer-motion'
import { ArrowRightIcon, CheckIcon, CloudIcon, ServerIcon, CubeIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'
import { Link } from 'react-router-dom'

export default function SolucionesCloud() {
  const features = [
    {
      icon: CloudIcon,
      title: "Migración a la Nube",
      description: "Transición segura y eficiente de infraestructura on-premise a cloud"
    },
    {
      icon: ServerIcon,
      title: "Arquitecturas Serverless",
      description: "Soluciones escalables que se adaptan automáticamente a la demanda"
    },
    {
      icon: CubeIcon,
      title: "Microservicios",
      description: "Arquitecturas modulares y escalables para aplicaciones empresariales"
    },
    {
      icon: ShieldCheckIcon,
      title: "Seguridad Cloud",
      description: "Implementación de mejores prácticas de seguridad en la nube"
    }
  ]

  const services = [
    "Consultoría y estrategia cloud",
    "Migración de aplicaciones legacy",
    "Arquitectura multi-cloud y híbrida",
    "DevOps y CI/CD pipelines",
    "Monitoreo y optimización de costos",
    "Backup y disaster recovery"
  ]

  const cloudProviders = [
    { name: "AWS", category: "Enterprise" },
    { name: "Azure", category: "Microsoft" },
    { name: "Google Cloud", category: "Google" },
    { name: "DigitalOcean", category: "Simplicity" },
    { name: "Vercel", category: "Frontend" },
    { name: "Firebase", category: "Mobile" },
    { name: "Supabase", category: "Backend" },
    { name: "Railway", category: "Deploy" }
  ]

  const benefits = [
    {
      metric: "70%",
      label: "Reducción en costos de infraestructura"
    },
    {
      metric: "99.9%",
      label: "Disponibilidad garantizada del sistema"
    },
    {
      metric: "10x",
      label: "Velocidad de escalamiento automático"
    },
    {
      metric: "85%",
      label: "Mejora en tiempo de desarrollo"
    }
  ]

  const industries = [
    {
      name: "Startups Tech",
      description: "Infraestructura escalable desde el día uno sin inversión inicial alta",
      icon: "🚀"
    },
    {
      name: "E-commerce",
      description: "Manejo de picos de tráfico y escalamiento durante eventos especiales",
      icon: "🛒"
    },
    {
      name: "Fintech",
      description: "Cumplimiento regulatorio y seguridad bancaria en la nube",
      icon: "💳"
    },
    {
      name: "Healthcare",
      description: "Protección de datos sensibles con compliance HIPAA y similares",
      icon: "🏥"
    },
    {
      name: "Educación",
      description: "Plataformas educativas que escalan con el número de estudiantes",
      icon: "🎓"
    },
    {
      name: "Media & Streaming",
      description: "Distribución global de contenido con baja latencia",
      icon: "📺"
    }
  ]

  return (
    <div 
      className="min-h-screen"
      style={{
        background: 'linear-gradient(135deg, #0a0a0a 0%, #0a1a2e 50%, #0a0a0a 100%)',
        color: '#ffffff'
      }}
    >
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div 
            className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl"
            style={{ background: 'rgba(59, 130, 246, 0.1)' }}
          />
          <div 
            className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl"
            style={{ background: 'rgba(147, 197, 253, 0.1)' }}
          />
        </div>

        {/* Cloud Animation */}
        <div className="absolute inset-0 opacity-20">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: '40px',
                height: '40px'
              }}
              animate={{
                y: [-20, 20, -20],
                opacity: [0.3, 1, 0.3],
                scale: [1, 1.1, 1]
              }}
              transition={{
                duration: 6 + Math.random() * 4,
                repeat: Infinity,
                ease: "easeInOut",
                delay: Math.random() * 3
              }}
            >
              <CloudIcon 
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  color: '#3b82f6' 
                }} 
              />
            </motion.div>
          ))}
        </div>

        <div className="relative pt-32 pb-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                  style={{
                    display: 'inline-block',
                    padding: '8px 20px',
                    background: 'rgba(59, 130, 246, 0.2)',
                    border: '1px solid rgba(59, 130, 246, 0.4)',
                    borderRadius: '25px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#3b82f6',
                    marginBottom: '24px'
                  }}
                >
                  Soluciones Cloud
                </motion.div>
                
                <motion.h1
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  style={{
                    fontSize: '48px',
                    fontWeight: '800',
                    lineHeight: '1.1',
                    color: '#ffffff',
                    marginBottom: '24px'
                  }}
                  className="lg:text-6xl"
                >
                  Infraestructura{' '}
                  <span 
                    style={{
                      background: 'linear-gradient(135deg, #3b82f6, #93c5fd)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}
                  >
                    en la nube
                  </span>
                </motion.h1>
                
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  style={{
                    fontSize: '20px',
                    lineHeight: '1.6',
                    color: '#d1d5db',
                    marginBottom: '32px'
                  }}
                  className="text-xl lg:text-2xl"
                >
                  Transformamos tu infraestructura con soluciones cloud escalables, 
                  seguras y optimizadas que impulsan el crecimiento de tu negocio.
                </motion.p>
                
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="flex flex-col sm:flex-row gap-4"
                >
                  <Link to="/contacto">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      style={{
                        padding: '16px 32px',
                        background: 'linear-gradient(135deg, #3b82f6, #93c5fd)',
                        color: '#ffffff',
                        fontWeight: '600',
                        borderRadius: '12px',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Migrar a la Nube
                      <ArrowRightIcon style={{ width: '20px', height: '20px' }} />
                    </motion.button>
                  </Link>
                  <Link to="/casos">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      style={{
                        padding: '16px 32px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        color: '#ffffff',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '16px',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Ver Arquitecturas
                    </motion.button>
                  </Link>
                </motion.div>
              </motion.div>

              {/* Right Visual */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative flex items-center justify-center"
              >
                <div className="relative">
                  {/* Main Cloud */}
                  <motion.div
                    animate={{
                      y: [-10, 10, -10],
                      scale: [1, 1.05, 1]
                    }}
                    transition={{
                      duration: 6,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    style={{
                      width: '300px',
                      height: '200px',
                      background: 'linear-gradient(135deg, #3b82f6, #93c5fd)',
                      borderRadius: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}
                  >
                    <CloudIcon 
                      style={{ 
                        width: '120px', 
                        height: '120px', 
                        color: '#ffffff' 
                      }} 
                    />
                  </motion.div>

                  {/* Floating Data Nodes */}
                  {[
                    { top: '10%', left: '20%', delay: 0 },
                    { top: '20%', right: '10%', delay: 1 },
                    { bottom: '20%', left: '15%', delay: 2 },
                    { bottom: '15%', right: '20%', delay: 0.5 },
                    { top: '50%', left: '5%', delay: 1.5 },
                    { top: '40%', right: '5%', delay: 2.5 }
                  ].map((pos, index) => (
                    <motion.div
                      key={index}
                      className="absolute"
                      style={{
                        ...pos,
                        width: '60px',
                        height: '60px'
                      }}
                      animate={{
                        y: [-15, 15, -15],
                        opacity: [0.6, 1, 0.6]
                      }}
                      transition={{
                        duration: 4 + Math.random() * 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: pos.delay
                      }}
                    >
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          background: 'rgba(59, 130, 246, 0.8)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <ServerIcon 
                          style={{ 
                            width: '30px', 
                            height: '30px', 
                            color: '#ffffff' 
                          }} 
                        />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 
              style={{
                fontSize: '36px',
                fontWeight: '700',
                color: '#ffffff',
                marginBottom: '16px'
              }}
              className="text-4xl lg:text-5xl"
            >
              Servicios Cloud
            </h2>
            <p 
              style={{
                fontSize: '18px',
                color: '#d1d5db',
                maxWidth: '800px',
                margin: '0 auto'
              }}
              className="text-xl"
            >
              Ofrecemos soluciones cloud completas que se adaptan a las necesidades 
              específicas de tu negocio, desde startups hasta empresas enterprise
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -10 }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '16px',
                    padding: '32px',
                    textAlign: 'center',
                    transition: 'all 0.3s ease'
                  }}
                  className="group hover:border-blue-500/30"
                >
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    style={{
                      width: '64px',
                      height: '64px',
                      background: 'linear-gradient(135deg, #3b82f6, #93c5fd)',
                      borderRadius: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 24px auto'
                    }}
                  >
                    <IconComponent 
                      style={{ 
                        width: '32px', 
                        height: '32px', 
                        color: '#ffffff' 
                      }} 
                    />
                  </motion.div>
                  <h3 
                    style={{
                      fontSize: '20px',
                      fontWeight: '600',
                      color: '#ffffff',
                      marginBottom: '12px'
                    }}
                  >
                    {feature.title}
                  </h3>
                  <p 
                    style={{
                      fontSize: '16px',
                      color: '#d1d5db',
                      lineHeight: '1.6'
                    }}
                  >
                    {feature.description}
                  </p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section 
        className="py-20 px-6"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 
                style={{
                  fontSize: '36px',
                  fontWeight: '700',
                  color: '#ffffff',
                  marginBottom: '24px'
                }}
                className="text-4xl lg:text-5xl"
              >
                Servicios Especializados
              </h2>
              <p 
                style={{
                  fontSize: '18px',
                  color: '#d1d5db',
                  marginBottom: '32px'
                }}
                className="text-xl"
              >
                Desde consultoría estratégica hasta implementación completa, 
                cubrimos todo el espectro de necesidades cloud
              </p>
              
              <div className="space-y-4">
                {services.map((service, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '16px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    <CheckIcon 
                      style={{ 
                        width: '24px', 
                        height: '24px', 
                        color: '#3b82f6',
                        flexShrink: 0
                      }} 
                    />
                    <span 
                      style={{
                        fontSize: '16px',
                        color: '#ffffff',
                        fontWeight: '500'
                      }}
                    >
                      {service}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              {/* Cloud Providers Grid */}
              <div 
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '24px',
                  padding: '48px'
                }}
              >
                <h3 
                  style={{
                    fontSize: '24px',
                    fontWeight: '600',
                    color: '#ffffff',
                    marginBottom: '24px',
                    textAlign: 'center'
                  }}
                >
                  Proveedores Cloud
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {cloudProviders.map((provider, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      whileHover={{ scale: 1.05 }}
                      style={{
                        padding: '16px',
                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(147, 197, 253, 0.1))',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        textAlign: 'center',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <h4 
                        style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#ffffff',
                          marginBottom: '4px'
                        }}
                      >
                        {provider.name}
                      </h4>
                      <span 
                        style={{
                          fontSize: '12px',
                          color: '#3b82f6',
                          fontWeight: '500',
                          padding: '2px 8px',
                          background: 'rgba(59, 130, 246, 0.2)',
                          borderRadius: '6px'
                        }}
                      >
                        {provider.category}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 
              style={{
                fontSize: '36px',
                fontWeight: '700',
                color: '#ffffff',
                marginBottom: '16px'
              }}
              className="text-4xl lg:text-5xl"
            >
              Beneficios Comprobados
            </h2>
            <p 
              style={{
                fontSize: '18px',
                color: '#d1d5db',
                maxWidth: '800px',
                margin: '0 auto'
              }}
              className="text-xl"
            >
              Nuestras implementaciones cloud generan resultados medibles 
              en eficiencia, costos y disponibilidad
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                style={{
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(147, 197, 253, 0.1))',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '16px',
                  padding: '32px',
                  textAlign: 'center'
                }}
              >
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{
                    fontSize: '48px',
                    fontWeight: '800',
                    color: '#3b82f6',
                    marginBottom: '12px'
                  }}
                  className="text-5xl lg:text-6xl"
                >
                  {benefit.metric}
                </motion.div>
                <p 
                  style={{
                    fontSize: '16px',
                    color: '#ffffff',
                    fontWeight: '500',
                    lineHeight: '1.5'
                  }}
                >
                  {benefit.label}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section 
        className="py-20 px-6"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 
              style={{
                fontSize: '36px',
                fontWeight: '700',
                color: '#ffffff',
                marginBottom: '16px'
              }}
              className="text-4xl lg:text-5xl"
            >
              Industrias que Transformamos
            </h2>
            <p 
              style={{
                fontSize: '18px',
                color: '#d1d5db',
                maxWidth: '800px',
                margin: '0 auto'
              }}
              className="text-xl"
            >
              Soluciones cloud especializadas para diferentes sectores empresariales
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '16px',
                  padding: '32px',
                  transition: 'all 0.3s ease'
                }}
              >
                <div 
                  style={{
                    fontSize: '48px',
                    marginBottom: '16px',
                    textAlign: 'center'
                  }}
                >
                  {industry.icon}
                </div>
                <h3 
                  style={{
                    fontSize: '24px',
                    fontWeight: '600',
                    color: '#3b82f6',
                    marginBottom: '16px',
                    textAlign: 'center'
                  }}
                >
                  {industry.name}
                </h3>
                <p 
                  style={{
                    fontSize: '16px',
                    color: '#d1d5db',
                    lineHeight: '1.6',
                    textAlign: 'center'
                  }}
                >
                  {industry.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            style={{
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(147, 197, 253, 0.2))',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '24px',
              padding: '48px'
            }}
          >
            <h2 
              style={{
                fontSize: '36px',
                fontWeight: '700',
                color: '#ffffff',
                marginBottom: '16px'
              }}
              className="text-4xl lg:text-5xl"
            >
              ¿Listo para la nube?
            </h2>
            <p 
              style={{
                fontSize: '20px',
                color: '#d1d5db',
                marginBottom: '32px',
                maxWidth: '600px',
                margin: '0 auto 32px auto'
              }}
            >
              Transformemos tu infraestructura con soluciones cloud que escalan con tu negocio
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contacto">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '16px 32px',
                    background: 'linear-gradient(135deg, #3b82f6, #93c5fd)',
                    color: '#ffffff',
                    fontWeight: '600',
                    borderRadius: '12px',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  Consulta Cloud Gratuita
                  <ArrowRightIcon style={{ width: '20px', height: '20px' }} />
                </motion.button>
              </Link>
              <Link to="/casos">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    padding: '16px 32px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    color: '#ffffff',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '16px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  Ver Arquitecturas
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
